// src/components/quiz/ProgressRing.tsx
import { motion } from 'framer-motion'
import type { Section } from '../../hooks/useQuizState'
import { SECTION_SIZES, SECTION_ORDER } from '../../hooks/useQuizState'

interface ProgressRingProps {
  section: Section
  questionIndex: number
}

export function ProgressRing({ section, questionIndex }: ProgressRingProps) {
  // We'll map the 4 main sections (A, B, C, D) to a horizontal segmented bar
  const sectionIdx = SECTION_ORDER.indexOf(section)
  
  const totalQuestions = SECTION_ORDER.reduce((sum, s) => sum + SECTION_SIZES[s], 0)
  const completedBefore = SECTION_ORDER.slice(0, sectionIdx).reduce((sum, s) => sum + SECTION_SIZES[s], 0)
  const overallProgress = (completedBefore + questionIndex + 1) / totalQuestions

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-end',
      gap: '0.35rem',
      width: '100px', // width of the entire segment block
    }}>
      <div style={{
        display: 'flex',
        gap: '4px',
        width: '100%',
        height: '6px',
      }}>
        {SECTION_ORDER.map((sec, i) => {
          // Calculate the fill width (0 to 100%) for this specific segment
          let fillWidth = '0%'
          if (i < sectionIdx) {
            fillWidth = '100%' // Completed segments
          } else if (i === sectionIdx) {
            // Current segment progress safely calculated
            const safeSize = SECTION_SIZES[sec] || 1
            fillWidth = `${Math.min(100, ((questionIndex + 1) / safeSize) * 100)}%`
          } else {
            fillWidth = '0%' // Future segments
          }

          return (
            <div
              key={sec}
              style={{
                flex: 1,
                background: 'rgba(148,163,184,0.2)', // muted track
                borderRadius: '4px',
                overflow: 'hidden',
              }}
            >
              <motion.div
                initial={false}
                animate={{ width: fillWidth }}
                transition={{ duration: 0.4, ease: 'easeOut' }}
                style={{
                  height: '100%',
                  background: 'var(--color-teal)',
                  borderRadius: '4px',
                }}
              />
            </div>
          )
        })}
      </div>
      <span style={{
        fontSize: '0.65rem',
        color: 'var(--color-muted)',
        letterSpacing: '0.05em',
        fontWeight: 600,
      }}>
        {Math.round(overallProgress * 100)}%
      </span>
    </div>
  )
}
